import { useState, useEffect, useCallback, useRef } from 'react';

export interface DeviceInfo {
  deviceId: string;
  label: string;
}

export function useMediaDevices() {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [audioDevices, setAudioDevices] = useState<DeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<DeviceInfo[]>([]);
  const [selectedAudioId, setSelectedAudioId] = useState<string>('');
  const [selectedVideoId, setSelectedVideoId] = useState<string>('');
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const getDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audio = devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 5)}` }));
      const video = devices
        .filter((d) => d.kind === 'videoinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 5)}` }));

      setAudioDevices(audio);
      setVideoDevices(video);
      if (audio.length && !selectedAudioId) setSelectedAudioId(audio[0].deviceId);
      if (video.length && !selectedVideoId) setSelectedVideoId(video[0].deviceId);
    } catch (e) {
      console.warn('Unable to enumerate devices:', e);
    }
  }, [selectedAudioId, selectedVideoId]);

  const startMedia = useCallback(
    async (audio = true, video = true) => {
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }

        const constraints: MediaStreamConstraints = {
          audio: audio ? (selectedAudioId ? { deviceId: { exact: selectedAudioId } } : true) : false,
          video: video
            ? selectedVideoId
              ? { deviceId: { exact: selectedVideoId }, width: { ideal: 1280 }, height: { ideal: 720 } }
              : { width: { ideal: 1280 }, height: { ideal: 720 } }
            : false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        setLocalStream(stream);
        setIsAudioMuted(!audio);
        setIsVideoOff(!video);

        await getDevices();
        return stream;
      } catch (e) {
        console.error('Error starting media stream:', e);
        // Fallback with basic constraints if exact device failed
        try {
          const fallback = await navigator.mediaDevices.getUserMedia({ audio, video });
          streamRef.current = fallback;
          setLocalStream(fallback);
          setIsAudioMuted(!audio);
          setIsVideoOff(!video);
          return fallback;
        } catch (err) {
          console.error('Fallback getUserMedia failed:', err);
          return null;
        }
      }
    },
    [selectedAudioId, selectedVideoId, getDevices]
  );

  const toggleAudio = useCallback(() => {
    if (streamRef.current) {
      const audioTracks = streamRef.current.getAudioTracks();
      const nextState = !isAudioMuted;
      audioTracks.forEach((track) => {
        track.enabled = !nextState;
      });
      setIsAudioMuted(nextState);
    }
  }, [isAudioMuted]);

  const toggleVideo = useCallback(() => {
    if (streamRef.current) {
      const videoTracks = streamRef.current.getVideoTracks();
      const nextState = !isVideoOff;
      videoTracks.forEach((track) => {
        track.enabled = !nextState;
      });
      setIsVideoOff(nextState);
    }
  }, [isVideoOff]);

  const stopMedia = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setLocalStream(null);
    }
  }, []);

  useEffect(() => {
    return () => {
      stopMedia();
    };
  }, [stopMedia]);

  return {
    localStream,
    audioDevices,
    videoDevices,
    selectedAudioId,
    selectedVideoId,
    setSelectedAudioId,
    setSelectedVideoId,
    isAudioMuted,
    isVideoOff,
    startMedia,
    stopMedia,
    toggleAudio,
    toggleVideo,
    getDevices,
  };
}
